import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, FileText, Plus, Search, Trash2, UploadCloud } from 'lucide-react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { API_CONFIG } from '../../../config/api.config'
import { genId } from '../lib/id'
import type { VideoCheckpoint, VideoQuestion, VideoQuestionKind } from '../types/LessonDoc'

declare global {
  interface Window {
    YT?: any
    onYouTubeIframeAPIReady?: () => void
  }
}

const API_ROOT = API_CONFIG.python.apiUrl

const kinds: { value: VideoQuestionKind; label: string }[] = [
  { value: 'mcq', label: 'Trac nghiem' },
  { value: 'true_false', label: 'Dung/Sai' },
  { value: 'short_answer', label: 'Tra loi ngan' },
]

function getToken(): string {
  return localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''
}

function normalizeText(value: string): string {
  return String(value || '').trim().replace(/\s+/g, ' ')
}

function sameAnswer(a: string, b: string): boolean {
  return normalizeText(a).toLowerCase() === normalizeText(b).toLowerCase()
}

// Bộ nhận diện Word/số hoá đánh dấu đáp án đúng bằng dấu * đầu phương án (vd "*Lực tương tác...").
const STAR_RE = /^\s*\*+\s*/
function cleanOpt(s?: string): string {
  return String(s || '').replace(STAR_RE, '').trim()
}
function sameOpt(a?: string, b?: string): boolean {
  return sameAnswer(cleanOpt(a), cleanOpt(b))
}

function stripAnswerLabel(text?: string): string {
  return String(text || '')
    .replace(/^\s*(?:dap\s*(?:an|so)|answer|tra\s*loi|loi\s*giai|huong\s*dan|hd|lg|giai)\s*[:.]?\s*/i, '')
    .trim()
}

function renderMathText(text?: string): string {
  const esc = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return esc
    .replace(/\$\$([^$]+)\$\$/g, (_m, f) => {
      try { return katex.renderToString(String(f).trim(), { displayMode: true, throwOnError: false }) } catch { return String(f) }
    })
    .replace(/\$([^$]+)\$/g, (_m, f) => {
      try { return katex.renderToString(String(f).trim(), { displayMode: false, throwOnError: false }) } catch { return String(f) }
    })
}

function renderInline(text?: string) {
  return <span dangerouslySetInnerHTML={{ __html: renderMathText(text) }} />
}

function renderRichContent(text?: string) {
  const raw = String(text || '')
  if (!raw) return null
  return raw.split(/\n{2,}/).map((block, bi) => {
    const lines = block.split('\n').map((x) => x.trim()).filter(Boolean)
    const tableLines = lines.filter((x) => x.includes('|'))
    if (tableLines.length >= 2) {
      const rows = tableLines
        .filter((line) => !/^\|?\s*:?-{3,}/.test(line))
        .map((line) => line.split('|').map((cell) => cell.trim()).filter((cell, i, arr) => !(cell === '' && (i === 0 || i === arr.length - 1))))
        .filter((row) => row.length)
      return (
        <div key={bi} className="my-2 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="border border-slate-200 px-2 py-1 align-top">{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    return (
      <p key={bi} className="mb-1 leading-relaxed">
        {lines.map((line, li) => (
          <React.Fragment key={li}>
            {renderInline(line)}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    )
  })
}

function normalizeKind(q: any, fallback: VideoQuestionKind = 'mcq'): VideoQuestionKind {
  const t = String(q?.type || q?.question_type || q?.loai || '').toUpperCase()
  if (t === 'DS' || q?.multi === true || q?.statement_a || q?.answer_a !== undefined) return 'true_false'
  if (t === 'TL' || t === 'TL_ESSAY' || t === 'SHORT_ANSWER' || q?.answer_value) return 'short_answer'
  if (q?.kind === 'short_answer' || q?.kind === 'true_false' || q?.kind === 'mcq') return q.kind
  return fallback
}

function parsedQuestionToVideoQuestion(q: any, index: number): VideoQuestion {
  const rawOpts = Array.isArray(q.options) ? q.options.filter(Boolean).map(String) : []
  const rawCorrect = Array.isArray(q.correctAnswers) ? q.correctAnswers.filter(Boolean).map(String) : []
  // Dấu * đầu phương án = đáp án đúng (quy ước bộ nhận diện) → bóc khỏi hiển thị, đưa vào correctAnswers.
  const starred = rawOpts.filter((o: string) => STAR_RE.test(o)).map(cleanOpt)
  const opts = rawOpts.map(cleanOpt)
  const correct = (rawCorrect.length ? rawCorrect.map(cleanOpt) : starred).filter(Boolean)
  const kind = normalizeKind(q, q.type === 'short_answer' ? 'short_answer' : q.multi ? 'true_false' : 'mcq')
  const solution = q.solution || q.guide || ''
  return {
    id: genId('vq'),
    kind,
    prompt: q.text || q.prompt || `Cau hoi ${index + 1}`,
    options: kind === 'short_answer' ? [] : opts,
    correctAnswers: kind === 'short_answer' ? [] : correct,
    answerText: kind === 'short_answer' ? (correct[0] || stripAnswerLabel(solution)) : '',
    image: q.image || '',
    solution,
  }
}

function bankQuestionToVideoQuestion(q: any, index: number): VideoQuestion {
  const kind = normalizeKind(q)
  if (kind === 'true_false') {
    const statements = ['a', 'b', 'c', 'd']
      .map((k) => ({ text: q[`statement_${k}`] || q[`option_${k}`] || '', ok: q[`answer_${k}`] }))
      .filter((x) => String(x.text || '').trim())
    return {
      id: genId('vq'),
      kind: 'true_false',
      prompt: q.question_lead || q.question || q.text || q.noi_dung || `Cau hoi ${index + 1}`,
      options: statements.map((x) => String(x.text)),
      correctAnswers: statements.filter((x) => x.ok === true || x.ok === 'true' || x.ok === 1 || x.ok === '1' || String(x.ok).toUpperCase() === 'D').map((x) => String(x.text)),
      image: q.image || q.image_link || q.question_image || '',
      solution: q.loi_giai || q.solution || q.explanation || '',
    }
  }
  if (kind === 'short_answer') {
    return {
      id: genId('vq'),
      kind: 'short_answer',
      prompt: q.question_lead || q.question || q.text || q.noi_dung || `Cau hoi ${index + 1}`,
      options: [],
      correctAnswers: [],
      answerText: q.answer_value || q.correct_answer || q.dap_an || '',
      image: q.image || q.image_link || q.question_image || '',
      solution: q.loi_giai || q.solution || q.explanation || '',
    }
  }
  const letters = ['a', 'b', 'c', 'd']
  const options = letters.map((k) => q[`option_${k}`] || q[`answer_${k}`] || '').filter((x) => String(x).trim()).map(String)
  const correctKey = String(q.correct_answer || q.dap_an || q.answer || '').trim().toUpperCase()
  const correct = /^[A-D]$/.test(correctKey) ? [options[correctKey.charCodeAt(0) - 65]].filter(Boolean) : options.filter((o) => sameAnswer(o, correctKey))
  return {
    id: genId('vq'),
    kind: 'mcq',
    prompt: q.question_lead || q.question || q.text || q.noi_dung || `Cau hoi ${index + 1}`,
    options,
    correctAnswers: correct,
    image: q.image || q.image_link || q.question_image || '',
    solution: q.loi_giai || q.solution || q.explanation || '',
  }
}

async function parseWordQuestions(file: File): Promise<VideoQuestion[]> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${API_ROOT}/active/parse-docx`, { method: 'POST', body: form })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(detail || `HTTP ${res.status}`)
  }
  const data = await res.json()
  return (data.questions || []).map((q: any, i: number) => ({ ...parsedQuestionToVideoQuestion(q, i), source: 'word' as const }))
}

async function fetchBankQuestions(
  scope: string, qtype: string, limit: number,
  grade?: string, chapter?: string, lesson?: string,
): Promise<VideoQuestion[]> {
  const qs = new URLSearchParams({ scope, limit: String(limit) })
  if (qtype !== 'ALL') qs.set('type', qtype)
  if (grade) qs.set('grade', grade)
  if (chapter) qs.set('chapter', chapter)
  if (lesson) qs.set('lesson', lesson)
  const res = await fetch(`${API_ROOT}/pmaker-bank/questions?${qs.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(detail || `HTTP ${res.status}`)
  }
  const data = await res.json()
  return (data.items || []).map((q: any, i: number) => ({ ...bankQuestionToVideoQuestion(q, i), source: 'pmaker' as const }))
}

function cloneQuestion(q: VideoQuestion): VideoQuestion {
  return {
    ...q,
    id: genId('vq'),
    options: q.options ? [...q.options] : [],
    correctAnswers: q.correctAnswers ? [...q.correctAnswers] : [],
  }
}

function emptyQuestion(): VideoQuestion {
  return {
    id: genId('vq'),
    kind: 'mcq',
    prompt: 'Nhap noi dung cau hoi...',
    options: ['A.', 'B.', 'C.', 'D.'],
    correctAnswers: [],
    answerText: '',
    solution: '',
    source: 'manual',
  }
}

export function createEmptyVideoCheckpointAt(timeSec = 30, question?: VideoQuestion): VideoCheckpoint {
  return {
    id: genId('vchk'),
    timeSec: Math.max(0, Math.round(timeSec * 10) / 10),
    maxAttempts: 2,
    question: cloneQuestion(question || emptyQuestion()),
  }
}

const QuestionPreview: React.FC<{ question: VideoQuestion; compact?: boolean }> = ({ question, compact }) => (
  <div className={`space-y-2 text-sm text-slate-800 ${compact ? 'text-xs' : ''}`}>
    <div className="leading-relaxed">{renderRichContent(question.prompt)}</div>
    {question.image && <img src={question.image} alt="" className="max-h-44 rounded border object-contain" />}
    {question.kind === 'short_answer' ? (
      <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-2 text-xs text-emerald-800">
        Dap an: {question.answerText || '(chua nhap)'}
      </div>
    ) : (
      <div className="grid gap-1">
        {(question.options || []).map((o, i) => {
          const ok = (question.correctAnswers || []).some((x) => sameOpt(x, o)) || STAR_RE.test(String(o))
          return (
            <div key={i} className={`rounded border px-2 py-1 ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-white'}`}>
              {String.fromCharCode(65 + i)}. {renderInline(cleanOpt(o))}
            </div>
          )
        })}
      </div>
    )}
    {question.solution && (
      <div className="rounded-lg border border-amber-100 bg-amber-50 p-2 text-xs text-amber-900">
        <div className="mb-1 font-black">Huong dan giai</div>
        <div>{renderRichContent(question.solution)}</div>
      </div>
    )}
  </div>
)

const QuestionEditorFields: React.FC<{
  question: VideoQuestion
  onChange: (question: VideoQuestion) => void
  hidePreview?: boolean
}> = ({ question, onChange, hidePreview }) => {
  const patch = (partial: Partial<VideoQuestion>) => onChange({ ...question, ...partial })
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="rounded border border-slate-200 px-2 py-1 text-sm"
          value={question.kind}
          onChange={(e) => patch({ kind: e.target.value as VideoQuestionKind })}
        >
          {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>
      <textarea
        className="min-h-[72px] w-full rounded-lg border border-slate-200 p-2 text-sm"
        value={question.prompt}
        onChange={(e) => patch({ prompt: e.target.value })}
        placeholder="Noi dung cau hoi, co the chua cong thuc $...$ hoac bang markdown"
      />
      {question.image && <img src={question.image} alt="Cau hoi" className="max-h-40 rounded border border-slate-200 bg-white object-contain" />}
      {question.kind === 'short_answer' ? (
        <input
          className="w-full rounded-lg border border-slate-200 p-2 text-sm"
          value={question.answerText || ''}
          onChange={(e) => patch({ answerText: e.target.value })}
          placeholder="Dap an ngan dung"
        />
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {(question.options || []).map((opt, oi) => (
            <label key={oi} className="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm">
              <input
                type={question.kind === 'mcq' ? 'radio' : 'checkbox'}
                checked={(question.correctAnswers || []).some((x) => sameAnswer(x, opt))}
                onChange={(e) => {
                  const opts = question.options || []
                  const cur = question.correctAnswers || []
                  const nextCorrect = question.kind === 'mcq'
                    ? [opts[oi]]
                    : e.target.checked
                      ? [...cur, opts[oi]]
                      : cur.filter((x) => !sameAnswer(x, opts[oi]))
                  patch({ correctAnswers: nextCorrect })
                }}
              />
              <input
                className="min-w-0 flex-1 rounded border border-slate-100 px-2 py-1"
                value={opt}
                onChange={(e) => {
                  const opts = [...(question.options || [])]
                  const old = opts[oi]
                  opts[oi] = e.target.value
                  const correct = (question.correctAnswers || []).map((x) => sameAnswer(x, old) ? e.target.value : x)
                  patch({ options: opts, correctAnswers: correct })
                }}
              />
            </label>
          ))}
        </div>
      )}
      <textarea
        className="min-h-[56px] w-full rounded-lg border border-slate-200 p-2 text-sm"
        value={question.solution || ''}
        onChange={(e) => patch({ solution: e.target.value })}
        placeholder="Huong dan giai / phan hoi sau cau hoi"
      />
      {!hidePreview && (
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <p className="mb-1 flex items-center gap-1 text-[11px] font-black uppercase text-slate-500"><FileText size={13} /> Xem truoc</p>
          <QuestionPreview question={question} />
        </div>
      )}
    </div>
  )
}

export const VideoQuestionEditor: React.FC<{
  checkpoints?: VideoCheckpoint[]
  questionPool?: VideoQuestion[]
  onChange: (next: VideoCheckpoint[]) => void
  onPoolChange?: (next: VideoQuestion[]) => void
  /** true = quản lý mốc bằng thanh timeline (ẩn danh sách mốc bên dưới cho gọn) */
  hideCheckpointList?: boolean
  /** trả về giây hiện tại của video — dùng khi thêm mốc từ danh sách cũ */
  attachAt?: () => number
}> = ({ checkpoints = [], questionPool = [], onChange, onPoolChange, hideCheckpointList, attachAt }) => {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [tab, setTab] = useState<'word' | 'manual' | 'pmaker'>('word')
  const [scope, setScope] = useState('both')
  const [qtype, setQtype] = useState('ALL')
  const [grade, setGrade] = useState('')
  const [chapter, setChapter] = useState('')
  const [lessonNo, setLessonNo] = useState('')
  const [search, setSearch] = useState('')
  const [pickerFor, setPickerFor] = useState<string | null>(null)

  const pool = questionPool || []
  const bySource = (source: 'word' | 'manual' | 'pmaker') => pool.filter((q) => (q.source || 'word') === source)
  const filteredPool = pool.filter((q) => normalizeText(q.prompt).toLowerCase().includes(normalizeText(search).toLowerCase()))

  const setPool = (next: VideoQuestion[]) => onPoolChange?.(next)
  const patchCheckpoint = (id: string, fn: (c: VideoCheckpoint) => VideoCheckpoint) => onChange(checkpoints.map((c) => (c.id === id ? fn(c) : c)))
  const removeCheckpoint = (id: string) => onChange(checkpoints.filter((c) => c.id !== id))

  const importWord = async (file?: File | null) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setMsg('Chi ho tro file Word .docx')
      return
    }
    setBusy(true)
    setMsg('')
    try {
      const imported = await parseWordQuestions(file)
      setPool([...pool, ...imported])
      setMsg(`Da nap ${imported.length} cau hoi vao kho cau hoi cua video.`)
    } catch (e) {
      setMsg(`Chua doc duoc file Word: ${(e as Error).message || e}`)
    } finally {
      setBusy(false)
    }
  }

  const importBank = async () => {
    setBusy(true)
    setMsg('')
    try {
      const imported = await fetchBankQuestions(scope, qtype, 80, grade, chapter, lessonNo)
      setPool([...pool, ...imported])
      setMsg(`Da nap ${imported.length} cau hoi tu ngan hang PMaker.`)
    } catch (e) {
      setMsg(`Chua nap duoc PMaker: ${(e as Error).message || e}`)
    } finally {
      setBusy(false)
    }
  }

  const addManualToPool = () => setPool([...pool, emptyQuestion()])
  const patchPoolQuestion = (id: string, next: VideoQuestion) => setPool(pool.map((q) => q.id === id ? next : q))
  const removePoolQuestion = (id: string) => setPool(pool.filter((q) => q.id !== id))

  const addCheckpoint = (question?: VideoQuestion) =>
    onChange([...checkpoints, createEmptyVideoCheckpointAt(attachAt ? attachAt() : 30, question || pool[0])])
  const chooseForCheckpoint = (checkpointId: string, question: VideoQuestion) => {
    patchCheckpoint(checkpointId, (cp) => ({ ...cp, question: cloneQuestion(question) }))
    setPickerFor(null)
  }

  const tabBtn = (key: 'word' | 'manual' | 'pmaker', icon: React.ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-black transition
        ${tab === key ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-blue-50'}`}
    >
      {icon} {label}
    </button>
  )

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3 space-y-3">
      <div>
        <p className="text-sm font-black text-blue-900">Câu hỏi chèn vào video</p>
        <p className="text-xs text-blue-700">Kéo-thả Word để nạp và xem số câu; muốn chèn vào video thì bấm <b>Chèn câu hỏi tại...</b> dưới video.</p>
      </div>

      {/* 3 nguồn câu hỏi — chọn 1 tab */}
      <div className="grid grid-cols-3 gap-2">
        {tabBtn('word', <UploadCloud size={14} />, `Từ file Word (${bySource('word').length})`)}
        {tabBtn('manual', <Plus size={14} />, `Tự gõ (${bySource('manual').length})`)}
        {tabBtn('pmaker', <Search size={14} />, `Từ PMaker (${bySource('pmaker').length})`)}
      </div>

      <div className="rounded-lg border border-blue-100 bg-white p-3">
        {tab === 'word' && (
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); importWord(e.dataTransfer.files?.[0]) }}
            className="flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 px-4 py-6 text-center hover:bg-blue-50"
          >
            <UploadCloud size={22} className="text-blue-500" />
            <span className="text-sm font-bold text-blue-700">Kéo-thả file Word (.docx) vào đây hoặc bấm để chọn</span>
            <span className="text-xs text-slate-500">Các câu hỏi trong file sẽ được nạp vào kho bên dưới</span>
            <input type="file" accept=".docx" className="hidden" onChange={(e) => importWord(e.target.files?.[0])} />
          </label>
        )}

        {tab === 'manual' && (
          <div className="flex flex-col items-center gap-2 py-3 text-center">
            <button type="button" onClick={() => { addManualToPool(); setMsg('Đã thêm câu hỏi trống — bấm vào câu ở danh sách dưới để soạn nội dung.') }} className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white hover:bg-blue-700">
              <Plus size={16} /> Thêm câu hỏi trống
            </button>
            <span className="text-xs text-slate-500">Sau đó bấm vào câu trong kho để gõ đề, phương án và chọn đáp án đúng</span>
          </div>
        )}

        {tab === 'pmaker' && (
          <div className="flex flex-wrap items-center gap-2">
            <select className="rounded border border-slate-200 px-2 py-1.5 text-xs font-bold" value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">Mọi lớp</option>
              <option value="10">Lớp 10</option>
              <option value="11">Lớp 11</option>
              <option value="12">Lớp 12</option>
            </select>
            <input type="number" min={1} className="w-20 rounded border border-slate-200 px-2 py-1.5 text-xs font-bold" value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="Chương" />
            <input type="number" min={1} className="w-16 rounded border border-slate-200 px-2 py-1.5 text-xs font-bold" value={lessonNo} onChange={(e) => setLessonNo(e.target.value)} placeholder="Bài" />
            <select className="rounded border border-slate-200 px-2 py-1.5 text-xs font-bold" value={qtype} onChange={(e) => setQtype(e.target.value)}>
              <option value="ALL">Mọi loại</option>
              <option value="TN">Trắc nghiệm</option>
              <option value="DS">Đúng/Sai</option>
              <option value="TL">Trả lời ngắn</option>
            </select>
            <select className="rounded border border-slate-200 px-2 py-1.5 text-xs font-bold" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="both">Cá nhân + dùng chung</option>
              <option value="personal">Cá nhân</option>
              <option value="shared">Dùng chung</option>
            </select>
            <button type="button" onClick={importBank} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-black text-white hover:bg-indigo-700 disabled:opacity-50">
              Nạp câu hỏi
            </button>
          </div>
        )}

        {busy && <p className="mt-2 text-xs font-semibold text-blue-700">Đang xử lý...</p>}
        {msg && <p className="mt-2 text-xs font-semibold text-slate-600">{msg}</p>}
      </div>

      {/* Kho câu hỏi đã nạp */}
      <div className="rounded-lg border border-blue-100 bg-white p-3">
        <div className="mb-2 flex items-center gap-2">
          <p className="text-xs font-black uppercase tracking-wide text-slate-500">Kho câu hỏi ({pool.length})</p>
          {pool.length > 4 && (
            <div className="ml-auto flex min-w-[180px] items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
              <Search size={13} className="text-slate-400" />
              <input className="min-w-0 flex-1 outline-none" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm trong kho..." />
            </div>
          )}
        </div>
        <div className="max-h-64 space-y-2 overflow-auto">
          {!pool.length ? (
            <div className="rounded-lg border border-dashed border-blue-200 bg-blue-50/50 p-4 text-center text-sm text-slate-500">
              Chưa có câu hỏi. Chọn 1 trong 3 cách phía trên để thêm.
            </div>
          ) : filteredPool.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-sm text-slate-500">Khong co cau phu hop tu khoa.</div>
          ) : (
            filteredPool.map((q, idx) => (
              <details key={q.id} className="rounded-lg border border-slate-200 bg-white p-2">
                <summary className="flex cursor-pointer list-none items-center gap-2 text-sm">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-black text-slate-600">#{idx + 1}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-slate-800">{q.prompt || '(chua co noi dung)'}</span>
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">{kinds.find((k) => k.value === q.kind)?.label}</span>
                  <button type="button" onClick={(e) => { e.preventDefault(); removePoolQuestion(q.id) }} className="rounded-lg p-1 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></button>
                </summary>
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <QuestionEditorFields question={q} onChange={(next) => patchPoolQuestion(q.id, next)} />
                </div>
              </details>
            ))
          )}
        </div>
      </div>

      {!hideCheckpointList && (
      <div className="rounded-lg border border-blue-100 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-black text-slate-800">Mốc dừng video</p>
            <p className="text-xs text-slate-500">Đúng: đi tiếp. Sai lần 1: làm lại. Sai lần 2: quay về đầu video.</p>
          </div>
          <button type="button" onClick={() => addCheckpoint()} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-bold text-white hover:bg-blue-700">
            <Plus size={14} /> Thêm mốc
          </button>
        </div>
        {!checkpoints.length ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">Chua co moc dung. Bam Gan moc o cau hoi hoac Them moc.</div>
        ) : (
          <div className="space-y-3">
            {[...checkpoints].sort((a, b) => a.timeSec - b.timeSec).map((cp, idx) => (
              <div key={cp.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-100 px-2 py-1 text-[11px] font-black text-blue-700">Moc #{idx + 1}</span>
                  <label className="text-xs font-bold text-slate-500">Giay</label>
                  <input
                    type="number"
                    min={0}
                    className="w-24 rounded border border-slate-200 px-2 py-1 text-sm"
                    value={cp.timeSec}
                    onChange={(e) => patchCheckpoint(cp.id, (c) => ({ ...c, timeSec: Math.max(0, Number(e.target.value) || 0) }))}
                  />
                  <button type="button" onClick={() => setPickerFor(pickerFor === cp.id ? null : cp.id)} className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-blue-700 ring-1 ring-blue-200 hover:bg-blue-50">
                    Chon cau tu kho
                  </button>
                  <button type="button" onClick={() => removeCheckpoint(cp.id)} className="ml-auto rounded-lg p-1.5 text-rose-600 hover:bg-rose-50">
                    <Trash2 size={16} />
                  </button>
                </div>
                {pickerFor === cp.id && (
                  <div className="mb-3 max-h-56 space-y-2 overflow-auto rounded-lg border border-blue-100 bg-white p-2">
                    {!pool.length ? (
                      <p className="p-3 text-center text-sm text-slate-500">Kho cau hoi dang trong.</p>
                    ) : pool.map((q, qi) => (
                      <button key={q.id} type="button" onClick={() => chooseForCheckpoint(cp.id, q)} className="flex w-full items-start gap-2 rounded-lg border border-slate-100 p-2 text-left hover:bg-blue-50">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-black text-slate-600">#{qi + 1}</span>
                        <span className="min-w-0 flex-1 text-xs text-slate-700">
                          <span className="block font-bold">{q.prompt}</span>
                          <span className="text-slate-400">{kinds.find((k) => k.value === q.kind)?.label}</span>
                        </span>
                        {sameAnswer(q.prompt, cp.question?.prompt || '') && <Check size={15} className="text-emerald-600" />}
                      </button>
                    ))}
                  </div>
                )}
                <QuestionEditorFields
                  question={cp.question}
                  onChange={(next) => patchCheckpoint(cp.id, (c) => ({ ...c, question: next }))}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  )
}

function fmtTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

// Thanh mốc kiểu iSpring: click lên thanh / bấm nút lớn → MỞ HỘP THOẠI chèn câu hỏi tại thời điểm đó;
// click chấm tròn → mở hộp thoại sửa mốc. KHÔNG tự tạo mốc khi chưa chọn câu (tránh mốc rác).
export const VideoCheckpointTimeline: React.FC<{
  videoRef: React.RefObject<HTMLVideoElement | null>
  src?: string
  checkpoints?: VideoCheckpoint[]
  onAdd: (timeSec: number) => void
  onEdit: (checkpointId: string) => void
}> = ({ videoRef, src, checkpoints = [], onAdd, onEdit }) => {
  const barRef = useRef<HTMLDivElement | null>(null)
  const [duration, setDuration] = useState(0)
  const [now, setNow] = useState(0)

  // Đọc thời lượng; webm từ MediaRecorder có duration=Infinity → ép tua xa để trình duyệt tính lại.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    let fixing = false
    const setDur = () => {
      if (isFinite(v.duration) && v.duration > 0) { setDuration(v.duration); return }
      if (!fixing) { fixing = true; try { v.currentTime = 1e10 } catch { /* noop */ } }
    }
    const onTime = () => {
      setNow(v.currentTime || 0)
      if (fixing && isFinite(v.duration) && v.duration > 0) {
        fixing = false
        setDuration(v.duration)
        try { v.currentTime = 0 } catch { /* noop */ }
      }
    }
    v.addEventListener('loadedmetadata', setDur)
    v.addEventListener('durationchange', setDur)
    v.addEventListener('timeupdate', onTime)
    if (v.readyState >= 1) setDur()
    return () => {
      v.removeEventListener('loadedmetadata', setDur)
      v.removeEventListener('durationchange', setDur)
      v.removeEventListener('timeupdate', onTime)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  const timeFromEvent = (e: React.MouseEvent): number | null => {
    const bar = barRef.current
    if (!bar || !duration) return null
    const rect = bar.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    return Math.round(ratio * duration * 10) / 10
  }

  const addAt = (e: React.MouseEvent) => {
    const t = timeFromEvent(e)
    if (t == null) return
    if (videoRef.current) { try { videoRef.current.currentTime = t; videoRef.current.pause() } catch { /* noop */ } }
    onAdd(t)
  }

  const addAtPlayhead = () => {
    if (videoRef.current) { try { videoRef.current.pause() } catch { /* noop */ } }
    onAdd(Math.round(now * 10) / 10)
  }

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
        <span>Click lên thanh tại chỗ muốn hỏi · click chấm tròn để sửa mốc</span>
        <span className="font-variant-numeric tabular-nums">{fmtTime(now)} / {duration ? fmtTime(duration) : '–:––'}</span>
      </div>
      <div
        ref={barRef}
        onClick={addAt}
        className={`relative h-8 rounded-lg border border-slate-200 bg-slate-100 ${duration ? 'cursor-crosshair' : 'cursor-wait'}`}
        title={duration ? 'Click để thêm mốc tại thời điểm này' : 'Đang đọc thời lượng video…'}
      >
        {/* vạch playhead */}
        {duration > 0 && (
          <div className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-rose-500/80" style={{ left: `${Math.min(100, (now / duration) * 100)}%` }} />
        )}
        {/* chấm mốc */}
        {duration > 0 && checkpoints.map((cp) => (
          <button
            key={cp.id}
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(cp.id) }}
            title={`${fmtTime(cp.timeSec)} · ${cp.question?.prompt || ''}`}
            className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow transition hover:scale-110 hover:bg-amber-500"
            style={{ left: `${Math.min(100, (cp.timeSec / duration) * 100)}%` }}
          />
        ))}
      </div>

      {/* Nút chính duy nhất: chèn câu hỏi tại vị trí đang phát */}
      <button
        type="button"
        onClick={addAtPlayhead}
        disabled={!duration}
        className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 px-4 py-3 text-sm font-black text-blue-700 transition hover:border-blue-400 hover:bg-blue-100 disabled:opacity-50"
      >
        <Plus size={17} /> Chèn câu hỏi tại {fmtTime(now)}
      </button>
    </div>
  )
}

// Hộp thoại "Chèn câu hỏi tại mốc" — nơi DUY NHẤT chọn/soạn câu hỏi: kho có sẵn / tự gõ / Word / PMaker.
export const QuestionInsertModal: React.FC<{
  open: boolean
  timeSec: number
  editing?: VideoCheckpoint | null
  questionPool?: VideoQuestion[]
  onPoolChange?: (next: VideoQuestion[]) => void
  onConfirm: (question: VideoQuestion, timeSec: number) => void
  onDelete?: () => void
  onClose: () => void
}> = ({ open, timeSec, editing, questionPool = [], onPoolChange, onConfirm, onDelete, onClose }) => {
  const pool = questionPool
  const [tab, setTab] = useState<'manual' | 'word' | 'pmaker'>('word')
  const [time, setTime] = useState(timeSec)
  const [draft, setDraft] = useState<VideoQuestion>(emptyQuestion())
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [scope, setScope] = useState('both')
  const [qtype, setQtype] = useState('ALL')
  const [grade, setGrade] = useState('')
  const [chapter, setChapter] = useState('')
  const [lessonNo, setLessonNo] = useState('')
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!open) { setShow(false); return }
    setTime(timeSec)
    setMsg('')
    if (editing) { setDraft({ ...editing.question }); setPickedId(null); setTab('manual') }
    else { setDraft(emptyQuestion()); setPickedId(null); setTab('word') }
    const id = requestAnimationFrame(() => setShow(true))
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id])

  if (!open) return null

  const pickFromPool = (q: VideoQuestion) => { setDraft(cloneQuestion(q)); setPickedId(q.id) }

  // Câu hỏi cũ (chưa gắn nhãn nguồn) tính vào Word — đa số được nạp từ Word trước đây.
  const bySrc = (s: 'word' | 'manual' | 'pmaker') => pool.filter((q) => (q.source || 'word') === s)

  const renderList = (items: VideoQuestion[]) => (
    !items.length ? null : (
      <div className="mt-3 space-y-2">
        {items.map((q) => (
          <button
            key={q.id}
            type="button"
            onClick={() => pickFromPool(q)}
            className={`flex w-full items-start gap-2 rounded-xl border p-3 text-left transition
              ${pickedId === q.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 bg-white hover:bg-blue-50/50'}`}
          >
            <span className="min-w-0 flex-1 text-sm">
              <span className="block font-semibold text-slate-800">{renderInline(cleanOpt(q.prompt))}</span>
              <span className="text-[11px] font-bold text-blue-600">{kinds.find((k) => k.value === q.kind)?.label}</span>
            </span>
            {pickedId === q.id && <Check size={17} className="mt-0.5 shrink-0 text-blue-600" />}
          </button>
        ))}
        {pickedId && items.some((q) => q.id === pickedId) && (
          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3">
            <p className="mb-1 text-[11px] font-black uppercase text-blue-600">Câu đã chọn — xem trước</p>
            <QuestionPreview question={draft} compact />
          </div>
        )}
      </div>
    )
  )

  const importWordFile = async (file?: File | null) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.docx')) { setMsg('Chỉ hỗ trợ file Word .docx'); return }
    setBusy(true); setMsg('')
    try {
      const imported = await parseWordQuestions(file)
      onPoolChange?.([...pool, ...imported])
      if (imported.length) {
        setDraft(cloneQuestion(imported[0]))
        setPickedId(imported[0].id)
      }
      setMsg(`Đã nạp ${imported.length} câu — bấm chọn 1 câu trong danh sách bên dưới.`)
    } catch (e) { setMsg(`Chưa đọc được file Word: ${(e as Error).message || e}`) } finally { setBusy(false) }
  }

  const importFromBank = async () => {
    setBusy(true); setMsg('')
    try {
      const imported = await fetchBankQuestions(scope, qtype, 80, grade, chapter, lessonNo)
      onPoolChange?.([...pool, ...imported])
      if (imported.length) {
        setDraft(cloneQuestion(imported[0]))
        setPickedId(imported[0].id)
      }
      setMsg(`Đã nạp ${imported.length} câu — bấm chọn 1 câu trong danh sách bên dưới.`)
    } catch (e) { setMsg(`Chưa nạp được PMaker: ${(e as Error).message || e}`) } finally { setBusy(false) }
  }

  const canConfirm = normalizeText(draft.prompt) !== '' && draft.prompt !== 'Nhap noi dung cau hoi...'

  const tabBtn = (key: typeof tab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`rounded-lg px-3 py-2 text-xs font-black transition
        ${tab === key ? 'bg-blue-600 text-white shadow' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-blue-50'}`}
    >
      {label}
    </button>
  )

  return (
    <div
      className={`fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/70 p-3 backdrop-blur-sm transition-opacity duration-200 sm:p-6 ${show ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-200 ease-out ${show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <span className="text-base font-black text-slate-800">{editing ? 'Sửa câu hỏi' : 'Chèn câu hỏi'} tại</span>
          <input
            type="number" min={0} step={0.1}
            className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-bold"
            value={time}
            onChange={(e) => setTime(Math.max(0, Number(e.target.value) || 0))}
          />
          <span className="text-sm font-bold text-slate-400">giây ({fmtTime(time)})</span>
          <button type="button" onClick={onClose} className="ml-auto rounded-lg p-2 text-slate-400 hover:bg-slate-100" title="Đóng">✕</button>
        </div>

        {/* 3 tab nguồn — mỗi tab đếm & hiển thị câu hỏi CỦA NGUỒN ĐÓ */}
        <div className="flex flex-wrap gap-2 border-b border-slate-100 px-4 py-2.5">
          {tabBtn('word', `📄 Nạp Word (${bySrc('word').length})`)}
          {tabBtn('manual', `✏️ ${editing ? 'Sửa nội dung' : 'Tự gõ'} (${bySrc('manual').length})`)}
          {tabBtn('pmaker', `🧩 Từ PMaker (${bySrc('pmaker').length})`)}
        </div>

        {/* Thân */}
        <div className="min-h-0 flex-1 overflow-auto p-4">
          {tab === 'manual' && (
            <>
              {bySrc('manual').length > 0 && !editing && (
                <div className="mb-3">
                  <p className="mb-1 text-[11px] font-black uppercase text-slate-500">Câu đã gõ trước — bấm để dùng lại</p>
                  {renderList(bySrc('manual'))}
                </div>
              )}
              <QuestionEditorFields question={draft} onChange={(next) => { setDraft(next); setPickedId(null) }} />
            </>
          )}

          {tab === 'word' && (
            <>
              <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); importWordFile(e.dataTransfer.files?.[0]) }}
                className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 px-4 py-8 text-center hover:bg-blue-50"
              >
                <UploadCloud size={26} className="text-blue-500" />
                <span className="text-sm font-bold text-blue-700">Kéo-thả file Word (.docx) vào đây hoặc bấm để chọn</span>
                <span className="text-xs text-slate-500">Đã nạp {bySrc('word').length} câu từ Word. Câu vừa nạp sẽ hiện ngay bên dưới.</span>
                <input type="file" accept=".docx" className="hidden" onChange={(e) => { importWordFile(e.target.files?.[0]); e.currentTarget.value = '' }} />
              </label>
              {bySrc('word').length > 0 ? (
                <div className="mt-3">
                  <p className="mb-1 text-[11px] font-black uppercase text-slate-500">Câu hỏi từ Word ({bySrc('word').length})</p>
                  {renderList(bySrc('word'))}
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                  Chưa có câu hỏi từ Word. Kéo-thả file .docx vào khung phía trên để nạp.
                </div>
              )}
            </>
          )}

          {tab === 'pmaker' && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <select className="rounded border border-slate-200 px-2 py-2 text-xs font-bold" value={grade} onChange={(e) => setGrade(e.target.value)}>
                  <option value="">Mọi lớp</option>
                  <option value="10">Lớp 10</option>
                  <option value="11">Lớp 11</option>
                  <option value="12">Lớp 12</option>
                </select>
                <input type="number" min={1} className="w-20 rounded border border-slate-200 px-2 py-2 text-xs font-bold" value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="Chương" />
                <input type="number" min={1} className="w-16 rounded border border-slate-200 px-2 py-2 text-xs font-bold" value={lessonNo} onChange={(e) => setLessonNo(e.target.value)} placeholder="Bài" />
                <select className="rounded border border-slate-200 px-2 py-2 text-xs font-bold" value={qtype} onChange={(e) => setQtype(e.target.value)}>
                  <option value="ALL">Mọi loại</option>
                  <option value="TN">Trắc nghiệm</option>
                  <option value="DS">Đúng/Sai</option>
                  <option value="TL">Trả lời ngắn</option>
                </select>
                <select className="rounded border border-slate-200 px-2 py-2 text-xs font-bold" value={scope} onChange={(e) => setScope(e.target.value)}>
                  <option value="both">Cá nhân + dùng chung</option>
                  <option value="personal">Cá nhân</option>
                  <option value="shared">Dùng chung</option>
                </select>
                <button type="button" onClick={importFromBank} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-black text-white hover:bg-indigo-700 disabled:opacity-50">
                  Nạp câu hỏi
                </button>
              </div>
              {bySrc('pmaker').length > 0 ? (
                <div className="mt-3">
                  <p className="mb-1 text-[11px] font-black uppercase text-slate-500">Câu hỏi từ PMaker ({bySrc('pmaker').length})</p>
                  {renderList(bySrc('pmaker'))}
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500">
                  Chưa có câu hỏi từ PMaker. Chọn bộ lọc rồi bấm Nạp câu hỏi.
                </div>
              )}
            </>
          )}

          {busy && <p className="mt-3 text-xs font-semibold text-blue-700">Đang xử lý...</p>}
          {msg && <p className="mt-3 text-xs font-semibold text-slate-600">{msg}</p>}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3">
          {editing && onDelete && (
            <button type="button" onClick={onDelete} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50">
              <Trash2 size={14} /> Xoá mốc
            </button>
          )}
          <button type="button" onClick={onClose} className="ml-auto rounded-lg px-4 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100">Huỷ</button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={() => onConfirm(draft, time)}
            className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {editing ? 'Cập nhật' : 'Chèn vào video'}
          </button>
        </div>
      </div>
    </div>
  )
}

function isCorrectAnswer(q: VideoQuestion, value: string | string[]): boolean {
  if (q.kind === 'short_answer') return sameAnswer(String(value || ''), q.answerText || '')
  const selected = (Array.isArray(value) ? value : [value]).map(cleanOpt).filter(Boolean)
  // So khớp SAU KHI bóc dấu * (tương thích câu đã lưu còn dấu *); thiếu correctAnswers → suy từ phương án có *.
  let correct = (q.correctAnswers || []).map(cleanOpt).filter(Boolean)
  if (!correct.length) correct = (q.options || []).filter((o) => STAR_RE.test(String(o))).map(cleanOpt)
  if (!correct.length) return false
  return selected.length === correct.length && selected.every((x) => correct.some((c) => sameAnswer(c, x)))
}

const GateOverlay: React.FC<{
  checkpoint: VideoCheckpoint
  attempt: number
  message: string
  onSubmit: (answer: string | string[]) => void
  /** 'correct' | 'failed' = đã trả lời xong → hiện lời giải + nút tiếp tục */
  result?: 'correct' | 'failed' | null
  onContinue?: () => void
}> = ({ checkpoint, attempt, message, onSubmit, result, onContinue }) => {
  const q = checkpoint.question
  const [single, setSingle] = useState('')
  const [multi, setMulti] = useState<string[]>([])
  const [text, setText] = useState('')
  // Hiệu ứng xuất hiện mượt: mount ẩn → frame sau hiện (fade + trượt lên nhẹ)
  const [show, setShow] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true))
    return () => cancelAnimationFrame(id)
  }, [])

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-3 sm:p-6
      transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}>
      <div className={`max-h-[92vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-4 shadow-2xl sm:p-6
        transition-all duration-300 ease-out ${show ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-6 scale-95 opacity-0'}`}>
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-black text-blue-700">Cau hoi tai {checkpoint.timeSec}s</span>
          <span className="text-xs font-bold text-slate-400">Lan thu {attempt + 1}/2</span>
        </div>
        {/* CHỈ hiện đề bài + ảnh — KHÔNG hiện đáp án đúng/lời giải (tránh lộ đáp án khi đang làm) */}
        <div className="space-y-2 text-sm text-slate-800">
          <div className="leading-relaxed">{renderRichContent(q.prompt)}</div>
          {q.image && <img src={q.image} alt="" className="max-h-44 rounded border object-contain" />}
        </div>
        {result ? (
          <div className="mt-4 space-y-3">
            <div className={`rounded-xl p-3 text-center text-base font-black ${result === 'correct' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {result === 'correct' ? '✓ Chính xác!' : '✗ Chưa đúng'}
            </div>
            {q.solution && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="mb-1 font-black">Lời giải</div>
                <div>{renderRichContent(q.solution)}</div>
              </div>
            )}
            <button type="button" onClick={onContinue} className="w-full rounded-xl bg-blue-600 px-4 py-3 text-base font-black text-white hover:bg-blue-700 active:scale-[0.99]">
              {result === 'correct' ? 'Tiếp tục ▶' : 'Xem lại từ đầu ↺'}
            </button>
          </div>
        ) : (
        <>
        <div className="mt-4 space-y-2">
          {q.kind === 'short_answer' ? (
            <input className="w-full rounded-lg border border-slate-200 p-2 text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="Nhap cau tra loi ngan..." />
          ) : q.kind === 'mcq' ? (
            (q.options || []).map((o, i) => (
              <label key={i} className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 p-3 hover:bg-blue-50 active:bg-blue-100">
                <input type="radio" name={q.id} checked={single === o} onChange={() => setSingle(o)} />
                <span>{String.fromCharCode(65 + i)}. {renderInline(cleanOpt(o))}</span>
              </label>
            ))
          ) : (
            (q.options || []).map((o, i) => (
              <label key={i} className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 p-3 hover:bg-blue-50 active:bg-blue-100">
                <input
                  type="checkbox"
                  checked={multi.includes(o)}
                  onChange={(e) => setMulti(e.target.checked ? [...multi, o] : multi.filter((x) => x !== o))}
                />
                <span>{String.fromCharCode(97 + i)}) {renderInline(cleanOpt(o))}</span>
              </label>
            ))
          )}
        </div>
        {message && <p className="mt-3 rounded-lg bg-amber-50 p-2 text-sm font-semibold text-amber-700">{message}</p>}
        <button type="button" onClick={() => onSubmit(q.kind === 'short_answer' ? text : q.kind === 'mcq' ? single : multi)} className="mt-4 w-full rounded-xl bg-blue-600 px-4 py-3 text-base font-black text-white hover:bg-blue-700 active:scale-[0.99]">
          Trả lời
        </button>
        </>
        )}
      </div>
    </div>
  )
}

export const InteractiveVideoPlayer: React.FC<{
  source: { kind: 'youtube'; youtubeId: string } | { kind: 'video'; src: string; isAudio?: boolean }
  title?: string
  checkpoints?: VideoCheckpoint[]
  onComplete?: () => void
}> = ({ source, title, checkpoints = [], onComplete }) => {
  const ordered = useMemo(() => [...(checkpoints || [])].sort((a, b) => a.timeSec - b.timeSec), [checkpoints])
  const videoRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null)
  const ytContainerRef = useRef<HTMLDivElement | null>(null)
  const ytPlayerRef = useRef<any>(null)
  const [active, setActive] = useState<VideoCheckpoint | null>(null)
  const [passed, setPassed] = useState<Set<string>>(new Set())
  const [attempts, setAttempts] = useState<Record<string, number>>({})
  const [message, setMessage] = useState('')
  const [result, setResult] = useState<'correct' | 'failed' | null>(null)
  const orderedRef = useRef<VideoCheckpoint[]>(ordered)
  const activeRef = useRef<VideoCheckpoint | null>(active)
  const passedRef = useRef<Set<string>>(passed)

  useEffect(() => { orderedRef.current = ordered }, [ordered])
  useEffect(() => { activeRef.current = active }, [active])
  useEffect(() => { passedRef.current = passed }, [passed])

  const pause = () => {
    if (source.kind === 'youtube') ytPlayerRef.current?.pauseVideo?.()
    else videoRef.current?.pause()
  }
  const play = () => {
    if (source.kind === 'youtube') ytPlayerRef.current?.playVideo?.()
    else videoRef.current?.play?.()
  }
  const seekStart = () => {
    if (source.kind === 'youtube') {
      ytPlayerRef.current?.seekTo?.(0, true)
      ytPlayerRef.current?.pauseVideo?.()
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.pause()
    }
  }
  const seekTo = (t: number) => {
    if (source.kind === 'youtube') ytPlayerRef.current?.seekTo?.(t, true)
    else if (videoRef.current) { try { videoRef.current.currentTime = t } catch { /* noop */ } }
  }
  const checkAt = (time: number) => {
    if (activeRef.current || !orderedRef.current.length) return
    const hit = orderedRef.current.find((cp) => !passedRef.current.has(cp.id) && time >= cp.timeSec)
    if (!hit) return
    // Kéo thanh trượt VƯỢT QUA mốc chưa trả lời → kéo về đúng mốc (không cho bỏ qua câu hỏi).
    if (time > hit.timeSec + 1) seekTo(hit.timeSec)
    // Đang fullscreen (mọi cách vào) → thoát để overlay câu hỏi che được toàn màn hình.
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => { /* noop */ })
    pause()
    setMessage('')
    setResult(null)
    setActive(hit)
  }

  useEffect(() => {
    if (source.kind !== 'youtube') return
    let timer: number | undefined
    const setup = () => {
      if (!window.YT?.Player || !ytContainerRef.current || ytPlayerRef.current) return
      ytPlayerRef.current = new window.YT.Player(ytContainerRef.current, {
        videoId: source.youtubeId,
        playerVars: { rel: 0, modestbranding: 1, enablejsapi: 1, fs: 0 },
        events: {
          onReady: () => {
            timer = window.setInterval(() => {
              const t = ytPlayerRef.current?.getCurrentTime?.()
              if (typeof t === 'number') checkAt(t)
            }, 400)
          },
          onStateChange: (e: any) => { if (e?.data === 0) onComplete?.() },
        },
      })
    }
    if (window.YT?.Player) setup()
    else {
      const prev = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => { prev?.(); setup() }
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script')
        script.src = 'https://www.youtube.com/iframe_api'
        document.body.appendChild(script)
      }
    }
    return () => {
      if (timer) window.clearInterval(timer)
      ytPlayerRef.current?.destroy?.()
      ytPlayerRef.current = null
    }
  }, [source.kind, source.kind === 'youtube' ? source.youtubeId : ''])

  const submit = (answer: string | string[]) => {
    if (!active) return
    if (isCorrectAnswer(active.question, answer)) {
      const nextPassed = new Set(passed)
      nextPassed.add(active.id)
      setPassed(nextPassed)
      setMessage('')
      if (active.question.solution) { setResult('correct'); return }   // có lời giải → hiện rồi mới Tiếp tục
      setActive(null)
      window.setTimeout(play, 100)
      return
    }
    const n = attempts[active.id] || 0
    if (n < 1) {
      setAttempts({ ...attempts, [active.id]: n + 1 })
      setMessage('Chưa đúng. Hãy thử lại một lần nữa.')
      return
    }
    // Sai lần 2: có lời giải → cho xem rồi mới quay về đầu video.
    setMessage('')
    if (active.question.solution) { setResult('failed'); return }
    setActive(null)
    setAttempts({})
    setPassed(new Set())
    seekStart()
  }

  const continueAfter = () => {
    if (result === 'correct') {
      setResult(null)
      setActive(null)
      window.setTimeout(play, 100)
      return
    }
    setResult(null)
    setActive(null)
    setAttempts({})
    setPassed(new Set())
    seekStart()
  }

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-black">
        {source.kind === 'youtube' ? (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <div ref={ytContainerRef} className="absolute inset-0 h-full w-full" title={title || 'YouTube video'} />
          </div>
        ) : source.isAudio ? (
          <div className="bg-white p-4">
            <audio ref={videoRef as React.RefObject<HTMLAudioElement>} controls preload="metadata" src={source.src} className="w-full" onTimeUpdate={(e) => checkAt(e.currentTarget.currentTime)} onSeeked={(e) => checkAt(e.currentTarget.currentTime)} onEnded={onComplete} />
          </div>
        ) : (
          <video
            ref={videoRef as React.RefObject<HTMLVideoElement>}
            controls
            controlsList="nofullscreen noremoteplayback"
            disablePictureInPicture
            preload="metadata"
            src={source.src}
            className="aspect-video w-full bg-black"
            onTimeUpdate={(e) => checkAt(e.currentTarget.currentTime)}
            onSeeked={(e) => checkAt(e.currentTarget.currentTime)}
            onDoubleClick={(e) => e.preventDefault()}
            onEnded={onComplete}
          />
        )}
      </div>
      {active && <GateOverlay checkpoint={active} attempt={attempts[active.id] || 0} message={message} onSubmit={submit} result={result} onContinue={continueAfter} />}
      {!!ordered.length && (
        <div className="mt-2 flex flex-wrap gap-1">
          {ordered.map((cp) => (
            <span key={cp.id} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${passed.has(cp.id) ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
              {cp.timeSec}s
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
